import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisProfileQuery } from '../api/useAnalysisProfileQuery';
import { useAnalysisVersionQuery } from '../api/useAnalysisVersionQuery';
import { getAnalysisVersionPath } from '../routes';
import { selectHasStagedChanges } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';

/**
 * Seeds the definition store from the analysis version, independent of which
 * detail tab is mounted. `setFromServer` no-ops on an already-loaded version id,
 * so local staged edits are never overwritten; the version query is shared and
 * deduped by TanStack.
 *
 * When the profile's latest version advances while the page is open on the prior
 * latest (an RA edit saves a new version), the route follows it so the definition
 * shows the RA's result. Staged local edits hold the route in place.
 */
export const useHydrateAnalysisDefinition = () => {
  const { projectId, analysisProfileId, analysisVersionId } = useAnalysisRouteParams();

  const versionQuery = useAnalysisVersionQuery({ projectId, analysisProfileId, analysisVersionId });

  const navigate = useNavigate();
  const { data: profileEnvelope } = useAnalysisProfileQuery({ projectId, analysisProfileId });
  const latestVersionId = profileEnvelope?.data.latestAnalysisVersionId;
  const hasStagedChanges = useAnalysisDefinitionStore(selectHasStagedChanges);
  const previousLatestVersionId = useRef(latestVersionId);

  useEffect(() => {
    const previous = previousLatestVersionId.current;
    previousLatestVersionId.current = latestVersionId;
    // Only follow an advance from the version being viewed, so a deliberately
    // opened older version URL is not redirected on first load.
    if (!latestVersionId || !previous || previous === latestVersionId) return;
    if (analysisVersionId !== previous || hasStagedChanges) return;
    navigate(getAnalysisVersionPath(projectId, analysisProfileId, latestVersionId), {
      replace: true,
    });
  }, [
    latestVersionId,
    analysisVersionId,
    hasStagedChanges,
    projectId,
    analysisProfileId,
    navigate,
  ]);

  const setFromServer = useAnalysisDefinitionStore((s) => s.setFromServer);
  const definition = versionQuery.data?.data.jsonValue ?? null;
  const hasData = versionQuery.isSuccess;

  useEffect(() => {
    if (!hasData) return;
    setFromServer({ versionId: analysisVersionId, definition });
  }, [hasData, analysisVersionId, definition, setFromServer]);

  return { versionQuery };
};
